import { useState, useEffect } from 'react';
import { detectDevice, DeviceInfo, DeviceType } from '../utils/deviceDetection';

/**
 * React hook for device detection
 * 
 * Provides reactive device information that updates when the window resizes.
 * Includes debouncing to prevent excessive re-renders during resize events.
 */
export function useDeviceDetection(debounceMs: number = 250): DeviceInfo {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => detectDevice());

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const handleResize = () => {
            // Debounce the resize event to prevent excessive re-renders
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setDeviceInfo(detectDevice());
            }, debounceMs);
        };

        // Listen for resize events
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        // Cleanup
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [debounceMs]);

    return deviceInfo;
}

/**
 * Simple hook that returns just the device type
 */
export function useDeviceType(debounceMs: number = 250): DeviceType {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.type;
}

/**
 * Hook for checking if the current device is mobile
 */
export function useIsMobile(debounceMs: number = 250): boolean {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.isMobile;
}

/**
 * Hook for checking if the current device is tablet
 */
export function useIsTablet(debounceMs: number = 250): boolean {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.isTablet;
}

/**
 * Hook for checking if the current device is desktop
 */
export function useIsDesktop(debounceMs: number = 250): boolean {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.isDesktop;
}

/**
 * Hook for checking if the device has touch capabilities
 */
export function useIsTouchDevice(debounceMs: number = 250): boolean {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.isTouchDevice;
}

/**
 * Hook for checking device orientation
 */
export function useOrientation(debounceMs: number = 250): 'portrait' | 'landscape' {
    const deviceInfo = useDeviceDetection(debounceMs);
    return deviceInfo.orientation;
}

/**
 * Hook for checking if device is in landscape mode
 */
export function useIsLandscape(debounceMs: number = 250): boolean {
    const orientation = useOrientation(debounceMs);
    return orientation === 'landscape';
}

/**
 * Hook for checking if device is in portrait mode
 */
export function useIsPortrait(debounceMs: number = 250): boolean {
    const orientation = useOrientation(debounceMs);
    return orientation === 'portrait';
}

/**
 * Hook for getting screen dimensions
 */
export function useScreenSize(debounceMs: number = 250): { width: number; height: number } {
    const deviceInfo = useDeviceDetection(debounceMs);
    return {
        width: deviceInfo.screenWidth,
        height: deviceInfo.screenHeight
    };
} 